import History from '@history';
import { createAsyncThunk, EntityId } from '@reduxjs/toolkit';
import { routesUrl } from 'app/fuse-configs/navigationConfig';
import jwtService from 'app/services/jwtService';
import { RootState } from 'app/store';
import { showMessage } from 'app/store/fuse/messageSlice';
import i18n from 'i18n';
import { selectUserFranchise } from '../../franchises/ducks/franchises.selector';
import { createGameApi, fetchGameInfoApi, fetchGameRewardsApi, fetchGamesApi, updateGameStatusApi } from '../api';
import { convertGameFormToGameDTO } from '../types';
import { currentGameSelector, selectRewards } from './games.selector';
import { setGameValidationDialog } from './games.slice';

export const fetchGamesThunk = createAsyncThunk(
  'games/fetchGamesThunk',
  async ({ page, size }: { page?: number; size?: number }, { dispatch, rejectWithValue }) => {
    try {
      return await fetchGamesApi(page, size);
    } catch (error) {
      dispatch(showMessage({ message: 'Error fetching games', variant: 'error' }));
      return rejectWithValue(error);
    }
  },
);

export const createNewGameThunk = createAsyncThunk(
  'game/createGame',
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      dispatch(setGameValidationDialog({ isLoading: true }));
      const rootState = getState() as RootState;
      const userId = jwtService.getUserId();

      if (!userId) {
        throw new Error('No user id');
      }
      const campaign = currentGameSelector(rootState);
      const rewards = selectRewards(rootState);
      const franchise = selectUserFranchise(rootState);

      const gameDTO = convertGameFormToGameDTO(campaign.form, rewards, userId, franchise.id);

      const gameIn = await createGameApi(gameDTO);

      History.push(routesUrl.gamesHistory);
      dispatch(
        showMessage({ message: i18n.t('games.creation.validation.success', { ns: 'games' }), variant: 'success' }),
      );
      return gameIn;
    } catch (error: any) {
      dispatch(showMessage({ message: i18n.t('games.creation.validation.error', { ns: 'games' }), variant: 'error' }));
      return rejectWithValue(error);
    }
  },
);

export const getGameInfoThunk = createAsyncThunk(
  'game/getGameInfoThunk',
  async (gameId: EntityId, { dispatch, rejectWithValue }) => {
    try {
      return await fetchGameInfoApi(gameId);
    } catch (error) {
      dispatch(showMessage({ message: 'Error fetching game info', variant: 'error' }));
      return rejectWithValue(error);
    }
  },
);

export const getGameRewardsThunk = createAsyncThunk(
  'game/getGameRewardsThunk',
  async (
    { gameId, includeDisabled }: { gameId: EntityId; includeDisabled?: boolean },
    { dispatch, rejectWithValue },
  ) => {
    try {
      return await fetchGameRewardsApi(gameId, includeDisabled);
    } catch (error) {
      dispatch(showMessage({ message: 'Error fetching game rewards', variant: 'error' }));
      return rejectWithValue(error);
    }
  },
);

export const updateGameStatusThunk = createAsyncThunk(
  'game/updateGameStatusThunk',
  async ({ gameId, disabled }: { gameId: EntityId; disabled: boolean }, { dispatch, rejectWithValue }) => {
    try {
      const updatedGame = await updateGameStatusApi(gameId, disabled);
      dispatch(
        showMessage({
          message: i18n.t(
            disabled ? 'games.detail.status.statusChangeDisabled' : 'games.detail.status.statusChangeEnabled',
            { ns: 'games' },
          ),
          variant: 'success',
        }),
      );
      return updatedGame;
    } catch (error) {
      dispatch(
        showMessage({
          message: i18n.t('games.detail.status.statusChangeError', { ns: 'games' }),
          variant: 'error',
        }),
      );
      return rejectWithValue(error);
    }
  },
);
